import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { QuestionMarkCircleIcon } from '@heroicons/react/24/outline';

const toc = [
  { id: 'overview', title: 'Overview' },
  { id: 'availability', title: 'Availability' },
  { id: 'training-sessions', title: 'Training Sessions' },
  { id: 'planner', title: 'Planner' },
  { id: 'autoplanner', title: 'Autoplanner' },
  { id: 'training-groups', title: 'Training Groups' },
  { id: 'crews', title: 'Crews' },
  { id: 'pwa', title: 'PWA (Install, Offline, Notifications)' },
  { id: 'notifications', title: 'Notifications' },
  { id: 'settings', title: 'Settings' },
  { id: 'use-cases', title: 'Use Cases' },
];

export default function Help() {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const el = document.getElementById(location.hash.slice(1));
      if (el) {
        // small delay to allow layout paint
        setTimeout(() => {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 0);
      }
    }
  }, [location.hash]);

  return (
    <div className="flex gap-6 py-6">
      <aside className="sticky top-20 hidden h-fit w-64 shrink-0 self-start rounded-lg border border-gray-200 bg-white p-4 md:block">
        <div className="mb-2 flex items-center gap-2 text-gray-700">
          <QuestionMarkCircleIcon className="h-5 w-5" />
          <span className="font-semibold">Help Center</span>
        </div>
        <nav className="space-y-1 text-sm">
          {toc.map((item) => (
            <a
              key={item.id}
              href={`#${item.id}`}
              className="block rounded px-2 py-1 text-gray-600 hover:bg-gray-50 hover:text-gray-900"
            >
              {item.title}
            </a>
          ))}
        </nav>
      </aside>

      <div className="flex-1">
        <header className="mb-6 rounded-lg bg-white p-6 shadow-sm">
          <h1 className="text-2xl font-bold text-gray-900">Help Center</h1>
          <p className="mt-1 text-gray-600">
            Learn how the app works and how to use each part effectively. Use the table of contents
            to jump to a topic, or click the question mark in the navbar for page-specific help.
          </p>
        </header>

        <div className="space-y-10">
          <section id="overview" className="scroll-mt-24 rounded-lg bg-white p-6 shadow-sm">
            <h2 className="text-xl font-semibold text-gray-900">Overview</h2>
            <p className="mt-2 text-gray-700">
              This app helps rowing clubs plan and manage training: set personal availability,
              create training sessions, plan them on a daily grid, manage crews and training groups,
              and use the autoplanner to generate schedules faster. Install it as a PWA to get
              offline read-only access and notifications.
            </p>
            <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {[
                { href: '#availability', label: 'Availability' },
                { href: '#training-sessions', label: 'Training Sessions' },
                { href: '#planner', label: 'Planner' },
                { href: '#autoplanner', label: 'Autoplanner' },
                { href: '#training-groups', label: 'Training Groups' },
                { href: '#crews', label: 'Crews' },
                { href: '#use-cases', label: 'Use Cases' },
              ].map((x) => (
                <a
                  key={x.href}
                  href={x.href}
                  className="rounded-lg border border-gray-200 p-4 hover:border-blue-300 hover:bg-blue-50/30"
                >
                  <div className="font-medium text-gray-900">{x.label}</div>
                  <div className="text-sm text-gray-600">Short guides and best practices</div>
                </a>
              ))}
            </div>
          </section>

          <section id="availability" className="scroll-mt-24 rounded-lg bg-white p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">Availability</h2>
              <Link to="/availability" className="text-sm text-blue-600 hover:underline">
                Go to page
              </Link>
            </div>
            <p className="mt-2 text-gray-700">
              Set when you can train using a weekly grid (desktop) or day tabs (mobile). Use presets
              to add common slots quickly and mark preferred times to help coaches plan better.
            </p>
            <ul className="mt-3 list-inside list-disc text-gray-700">
              <li>Desktop: click/drag on 30-minute slots; use daily quick-add buttons</li>
              <li>Mobile: use day tabs, quick-add buttons, and add custom slots</li>
              <li>Mark preferred vs available to guide planning quality</li>
              <li>Availability is saved automatically</li>
            </ul>
            <div className="mt-3">
              <h3 className="text-md font-semibold text-gray-900">Best practices</h3>
              <ul className="mt-2 list-inside list-disc text-gray-700">
                <li>Enter recurring patterns first, then fine-tune exceptions</li>
                <li>Use preferred for realistic top-choice times only</li>
                <li>Review before selection phases and at exam/holiday periods</li>
                <li>
                  Use the "Crew-mates Availability" button to see when your crew-mates are
                  available.
                </li>
              </ul>
            </div>
            <div className="mt-4 text-sm">
              <Link to="/help#planner" className="text-blue-600 hover:underline">
                How availability is used in planning
              </Link>
            </div>
          </section>

          <section
            id="training-sessions"
            className="scroll-mt-24 rounded-lg bg-white p-6 shadow-sm"
          >
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">Training Sessions</h2>
              <Link to="/training-sessions" className="text-sm text-blue-600 hover:underline">
                Go to page
              </Link>
            </div>
            <p className="mt-2 text-gray-700">
              Create sessions with date, duration, and assigned crews. Sessions start as
              “unscheduled”. When you schedule subsets of crews, the app creates child sessions and
              keeps the remainder unscheduled for later. You can use the planner or autoplanner to
              schedule sessions.
            </p>
            <ul className="mt-3 list-inside list-disc text-gray-700">
              <li>Assign multiple crews to a session for flexible scheduling</li>
              <li>Split sessions: schedule some crews now, others later</li>
              <li>Parent/child hierarchy: scheduled variants stay linked to the parent</li>
              <li>Bulk import sessions with the Import Schedule wizard</li>
            </ul>
            <div className="mt-3">
              <h3 className="text-md font-semibold text-gray-900">How to use this page</h3>
              <ol className="mt-2 list-inside list-decimal text-gray-700">
                <li>
                  Click "Create Sessions" to create a new session or bulk import sessions with the
                  Import Schedule wizard
                </li>
                <li>Set the date, title, and duration</li>
                <li>Assign crews to the session</li>
                <li>Click "Save" to save the session</li>
                <li>You can also use the autoplanner to schedule sessions.</li>
              </ol>
            </div>
            <div className="mt-3">
              <h3 className="text-md font-semibold text-gray-900">
                How to use the bulk import wizard
              </h3>
              <ol className="mt-2 list-inside list-decimal text-gray-700">
                <li>Click "Import Sessions" to open the wizard</li>
                <li>Follow the instructions in the left panel</li>
                <li>Paste the LLM output in the right panel</li>
                <li>Assign crews to the sessions</li>
                <li>Fine-tune crew assignments, dates, titles and durations</li>
                <li>Click "Import" to import the sessions</li>
              </ol>
            </div>
            <div className="mt-4 text-sm">
              <Link to="/help#autoplanner" className="text-blue-600 hover:underline">
                Learn how the Autoplanner uses your sessions
              </Link>
            </div>
          </section>

          <section id="planner" className="scroll-mt-24 rounded-lg bg-white p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">Planner</h2>
              <Link to="/planner" className="text-sm text-blue-600 hover:underline">
                Go to page
              </Link>
            </div>
            <p className="mt-2 text-gray-700">
              Drag unscheduled sessions onto a daily availability grid. Filter by crew to focus your
              view. Scheduling a subset of crews creates a scheduled child session and leaves the
              remainder unscheduled.
            </p>
            <ul className="mt-3 list-inside list-disc text-gray-700">
              <li>Date navigation and crew filter at the top</li>
              <li>Click on an unscheduled session to filter only rowers of its crews</li>
              <li>Availability grid with color coding (preferred, available, not available)</li>
              <li>Three-step scheduling: crews → coaches → review</li>
              <li>Partial scheduling creates split sessions; remainder stays unscheduled</li>
            </ul>
            <div className="mt-3">
              <h3 className="text-md font-semibold text-gray-900">
                How to use the planning wizard
              </h3>
              <ol className="mt-2 list-inside list-decimal text-gray-700">
                <li>Drag an unscheduled session onto the grid.</li>
                <li>Select the crews to schedule.</li>
                <li>Drag-Drop lineups if there are experimental crews.</li>
                <li>Add a coach to the session (optional).</li>
                <li>Validate if the scheduling is possible and if there are any issues.</li>
                <li>Schedule the session.</li>
              </ol>
            </div>
            <div className="mt-3">
              <h3 className="text-md font-semibold text-gray-900">Color coding</h3>
              <ul className="mt-2 list-inside list-disc text-gray-700">
                <li>Emerald = preferred; Blue/Teal = available; Gray = not available</li>
                <li>Coach conflicts and capacity warnings appear during review</li>
              </ul>
            </div>
          </section>

          <section id="autoplanner" className="scroll-mt-24 rounded-lg bg-white p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">Autoplanner</h2>
              <Link to="/autoplanner" className="text-sm text-blue-600 hover:underline">
                Go to page
              </Link>
            </div>
            <p className="mt-2 text-gray-700">
              The wizard guides you through: selecting a training group, selecting sessions within a
              date range (and ordering multiple sessions on the same day), validating availability,
              altering rower availability, excluding crews and resolving issues, optionally locking
              seatings, choosing an optimization goal, and confirming the task for processing.
              You’ll receive a notification when results are ready. When the result is ready, you
              can make slight changes and schedule the sessions.
            </p>
            <ul className="mt-3 list-inside list-disc text-gray-700">
              <li>Step 1: Select Training Group</li>
              <li>Step 2: Select Sessions and Date Range (with per-day ordering)</li>
              <li>Step 3: Validate Availability (exclusions/resolutions; save draft if needed)</li>
              <li>Step 4: Alter Rower Availability (optional)</li>
              <li>Step 5: Exclude Crews (optional)</li>
              <li>Step 6: Locked Seatings (optional; conflicts validated)</li>
              <li>Step 7: Choose Goal (e.g., consistent crews, mixing, preferred availability)</li>
              <li>Step 8: Confirm Task → Processing → Results</li>
            </ul>
            <div className="mt-3">
              <h3 className="text-md font-semibold text-gray-900">Tips</h3>
              <ul className="mt-2 list-inside list-disc text-gray-700">
                <li>Start with smaller date ranges for faster results during trials</li>
                <li>Lock only must-have seatings; let the rest be flexible</li>
                <li>
                  Make sure your crews are correctly setup and all sessions have the correct crews
                  assigned.
                </li>
              </ul>
            </div>
          </section>

          <section id="training-groups" className="scroll-mt-24 rounded-lg bg-white p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">Training Groups</h2>
              <Link to="/training-groups" className="text-sm text-blue-600 hover:underline">
                Go to page
              </Link>
            </div>
            <p className="mt-2 text-gray-700">
              Organize rowers and equipment into groups that underpin crew formation and planning.
              Assign coaches, manage members, and keep status/skill information up to date.
            </p>
            <ul className="mt-3 list-inside list-disc text-gray-700">
              <li>Groups list on the left; details on the right</li>
              <li>Manage members and equipment (coming soon) per group</li>
              <li>Coach-to-rower ratios and safety/compliance considerations (coming soon)</li>
            </ul>
          </section>

          <section id="crews" className="scroll-mt-24 rounded-lg bg-white p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">Crews</h2>
              <Link to="/crews" className="text-sm text-blue-600 hover:underline">
                Go to page
              </Link>
            </div>
            <p className="mt-2 text-gray-700">
              Create crews, assign boat types, and drag-and-drop rowers into seats. Use
              <span className="font-medium"> experimental crews</span> for lineup testing during
              selections or ad-hoc substitutions.
            </p>
            <ul className="mt-3 list-inside list-disc text-gray-700">
              <li>
                Three-panel layout: crews list, lineup editor (with side validation), available
                rowers
              </li>
              <li>Visual status: complete, partial, or missing required positions</li>
              <li>Changes save automatically</li>
              <li>Convert experimental crews to standard after selections finalize</li>
            </ul>
            <div className="mt-3">
              <h3 className="text-md font-semibold text-gray-900">Best practices</h3>
              <ul className="mt-2 list-inside list-disc text-gray-700">
                <li>Keep names consistent (e.g., W8+ A, M4x Dev) for autoplanner filters</li>
                <li>Use notes to record trial outcomes and rationale (coming soon)</li>
              </ul>
            </div>
          </section>

          <section id="pwa" className="scroll-mt-24 rounded-lg bg-white p-6 shadow-sm">
            <h2 className="text-xl font-semibold text-gray-900">
              PWA (Install, Offline, Notifications)
            </h2>
            <p className="mt-2 text-gray-700">
              Install the app on your device for a native-like experience. When installed, the app
              runs standalone, supports push notifications, and provides read-only offline access to
              upcoming data.
            </p>
            <ul className="mt-3 list-inside list-disc text-gray-700">
              <li>Install from the navbar button or your browser’s “Install App”</li>
              <li>Offline: view-only; editing is disabled until you’re back online</li>
              <li>Notifications: enable after login; manage in Settings</li>
            </ul>
          </section>

          <section id="notifications" className="scroll-mt-24 rounded-lg bg-white p-6 shadow-sm">
            <h2 className="text-xl font-semibold text-gray-900">Notifications</h2>
            <p className="mt-2 text-gray-700">
              Get session reminders and updates. Coaches can receive daily digests for key actions.
              Configure your preferences in Settings.
            </p>
            <ul className="mt-3 list-inside list-disc text-gray-700">
              <li>Session reminders for rowers and coaches</li>
              <li>Autoplanner task status notifications</li>
              <li>Weekly planning summaries (optional)</li>
            </ul>
          </section>

          <section id="settings" className="scroll-mt-24 rounded-lg bg-white p-6 shadow-sm">
            <h2 className="text-xl font-semibold text-gray-900">Settings</h2>
            <p className="mt-2 text-gray-700">
              Manage personal preferences like notifications and account details. Some options
              depend on your role.
            </p>
            <ul className="mt-3 list-inside list-disc text-gray-700">
              <li>Notification channels</li>
              <li>Personal information</li>
              <li>Preferred side</li>
              <li>Language (coming soon)</li>
              <li>Calendar integration for personal sync (if enabled)</li>
            </ul>
          </section>

          <section id="use-cases" className="scroll-mt-24 rounded-lg bg-white p-6 shadow-sm">
            <h2 className="text-xl font-semibold text-gray-900">Use Cases</h2>
            <p className="mt-2 text-gray-700">
              End-to-end examples to help you structure your season and day-to-day planning.
            </p>

            <div className="mt-6 space-y-8">
              <div id="season-setup">
                <h3 className="text-lg font-semibold text-gray-900">1) Start of Season Setup</h3>
                <ol className="mt-2 list-inside list-decimal space-y-2 text-gray-700">
                  <li>
                    Create{' '}
                    <Link to="/training-groups" className="text-blue-600 hover:underline">
                      training groups
                    </Link>
                    , add members and coaches
                  </li>
                  <li>
                    Ask members to fill their{' '}
                    <Link to="/availability" className="text-blue-600 hover:underline">
                      availability
                    </Link>
                    ; set preferred vs available
                  </li>
                  <li>
                    Bulk add or{' '}
                    <Link to="/training-sessions" className="text-blue-600 hover:underline">
                      create training sessions
                    </Link>
                  </li>
                  <li>
                    Create initial{' '}
                    <Link to="/crews" className="text-blue-600 hover:underline">
                      experimental crews
                    </Link>{' '}
                    for trials
                  </li>
                  <li>
                    Use the{' '}
                    <Link to="/planner" className="text-blue-600 hover:underline">
                      Planner
                    </Link>{' '}
                    or{' '}
                    <Link to="/autoplanner" className="text-blue-600 hover:underline">
                      Autoplanner
                    </Link>{' '}
                    to schedule sessions.
                  </li>
                </ol>
              </div>

              <div id="selecting-crews">
                <h3 className="text-lg font-semibold text-gray-900">2) Selecting Crews</h3>
                <ol className="mt-2 list-inside list-decimal space-y-2 text-gray-700">
                  <li>Create or experimental crews per boat class</li>
                  <li>Schedule evaluation sessions, splitting by crew as needed</li>
                  <li>Track outcomes in crew notes; iterate lineups (coming soon)</li>
                  <li>
                    Run{' '}
                    <Link to="/autoplanner" className="text-blue-600 hover:underline">
                      Autoplanner
                    </Link>{' '}
                    to automatically schedule sessions and create lineups.
                  </li>
                </ol>
              </div>

              <div id="post-selection">
                <h3 className="text-lg font-semibold text-gray-900">3) When Selections Finish</h3>
                <ol className="mt-2 list-inside list-decimal space-y-2 text-gray-700">
                  <li>Convert experimental crews to standard.</li>
                  <li>Update training groups and coach assignments</li>
                </ol>
              </div>

              <div id="in-season">
                <h3 className="text-lg font-semibold text-gray-900">4) During the Season</h3>
                <ol className="mt-2 list-inside list-decimal space-y-2 text-gray-700">
                  <li>Use weekly Planner cycles; review availability shifts weekly</li>
                  <li>Make sure rowers fill in their availability</li>
                  <li>Handle changes by rescheduling.</li>
                  <li>Send reminders via Notifications; monitor conflicts</li>
                </ol>
              </div>

              <div id="temporary-crews">
                <h3 className="text-lg font-semibold text-gray-900">5) Temporary Crews</h3>
                <ol className="mt-2 list-inside list-decimal space-y-2 text-gray-700">
                  <li>Create temporary experimental crews for substitutes/guests</li>
                  <li>Schedule using Planner; keep notes on context and duration</li>
                  <li>Archive or merge back after use to keep the list clean</li>
                </ol>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
